import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { CommonModule, NgIf, NgClass } from '@angular/common';
import { DataService, Card } from '../../../services/data.service';
import { MatProgressSpinnerModule, MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule, MatCheckbox } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatTooltipModule, MatTooltip } from '@angular/material/tooltip';
import { MatMenuModule, MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { FontSizeService, FontSizeOption } from '../../../services/font-size.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SafeResourceUrl, SafeHtml } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { FileToView } from '../../../components/file-processing/file-processing.component';
import { FileService } from '../../../services/file.service';
import { FileOperationEventsService } from '../../../services/file-operation-events.service';
import { LoadingService } from '../../../services/loading.service';

@Component({
  selector: 'app-details-area',
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    NgClass,
    MatProgressSpinnerModule,
    MatProgressSpinner,
    MatCardModule,
    MatCheckboxModule,
    MatTabsModule,
    MatButtonModule,
    MatIconButton,
    MatTooltipModule,
    MatTooltip,
    MatMenuModule,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatSlideToggleModule,
    FormsModule,
    MatToolbar,
    MatIcon,
    MatDivider
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  templateUrl: './details-area.component.html',
  styleUrl: './details-area.component.css'
})
export class DetailsAreaComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isFullscreenMode = false;
  @Input() isMobileView = false;
  @Input() fileLoading = false; // Tracks when a file is being loaded

  // Input for showMains from parent component
  @Input() showMains = true;
  @Output() showMainsChange = new EventEmitter<boolean>();
  @Output() backToMain = new EventEmitter<void>();
  @Output() viewModeChange = new EventEmitter<string>();
  @Output() hideDetails = new EventEmitter<void>(); // New event to hide details area

  // Current view mode
  viewMode: 'split' | 'fullscreen' | 'popup' = 'split';

  // HTML viewer properties
  showRawHtml = false;

  // GIF viewer properties
  isGifPlaying = true;
  gifElement: HTMLImageElement | null = null;

  // Expose console to template
  console = window.console;

  // Show question flag
  showQuestion = false;

  // Show Hindi flag
  showHindi = false;

  // Selected tab index
  selectedTabIndex = 0;

  // Track if images are loaded
  answerImageLoaded = false;
  explanationImageLoaded = false;
  questionImageLoaded = false;

  // Track if images have errors
  answerImageError = false;
  explanationImageError = false;
  questionImageError = false;

  // Track Hindi image loading
  hindiAnswerImageLoaded = false;
  hindiExplanationImageLoaded = false;
  hindiQuestionImageLoaded = false;

  // Track Hindi image errors
  hindiAnswerImageError = false;
  hindiExplanationImageError = false;
  hindiQuestionImageError = false;

  // Track if Google Docs viewer is loaded
  googleDocsViewerLoaded = false;
  googleDocsViewerError = false;

  // Store Google Docs viewer URL
  googleDocsViewerUrl: SafeResourceUrl | null = null;
  officeFileLoading = true;
  officeViewerError = false;

  // Add destroy Subject for subscription management
  private destroy$ = new Subject<void>();

  // Add currentFontSize property
  currentFontSize: FontSizeOption = 'medium';

  // Create a setter for selectedCard to debug when it changes
  private _selectedCard: Card | null = null;

  // View menu layout direction
  viewMenuLayoutDirection: 'vertical' | 'horizontal' = 'vertical';

  // Track whether we're in a popup externally via the class
  private _popupClassActive = false;

  @Input()
  set selectedCard(card: Card | null) {
    console.warn('=========== DETAILS AREA: CARD UPDATED ===========');
    console.warn('Card received:', card ? 'Card ID: ' + card.id : 'null');

    if (card && card.fileData) {
      console.warn('FileData detected:');
      console.warn('File type:', card.fileData.fileType);
      console.warn('File name:', card.fileData.fileName);
      console.warn('Content type property:', card.fileData.contentType || 'not set');
      console.warn('Content actual type:', typeof card.fileData.content);

      if (card.fileData.fileType === 'txt') {
        console.warn('==================== TEXT FILE ARRIVED IN DETAILS AREA ====================');

        if (typeof card.fileData.content === 'string') {
          const content = card.fileData.content as string;
          console.warn('Content IS a string! Length:', content.length);

          if (content.length > 0) {
            console.warn('Content is NOT empty! First 100 chars:');
            console.warn('---BEGIN CONTENT SAMPLE---');
            console.warn(content.substring(0, 100));
            console.warn('---END CONTENT SAMPLE---');
          } else {
            console.warn('Content is an EMPTY string.');
          }
        } else {
          console.warn('Content is NOT a string! Actual value:', card.fileData.content);
          if (card.fileData.content === null) {
            console.warn('Content is NULL');
          } else if (card.fileData.content === undefined) {
            console.warn('Content is UNDEFINED');
          } else {
            console.warn('Content is some other type:', Object.prototype.toString.call(card.fileData.content));
          }
        }
        console.warn('=======================================================================');
      }
    }

    this._selectedCard = card;

    // We still need to reset image states when selected card changes
    this.resetImageStates();
  }

  get selectedCard(): Card | null {
    return this._selectedCard;
  }

  constructor(
    private dataService: DataService,
    private fontSizeService: FontSizeService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private fileService: FileService,
    private fileOpEvents: FileOperationEventsService,
    private loadingService: LoadingService
  ) { }

  // Method to toggle between HTML source and rendered view
  toggleHtmlView(): void {
    this.showRawHtml = !this.showRawHtml;
  }

  // Method to toggle GIF playback
  toggleGifPlayback(): void {
    if (!this.gifElement) {
      // Find the GIF element in the DOM
      this.gifElement = document.querySelector('.gif-viewer img') as HTMLImageElement;
    }

    if (this.gifElement) {
      if (this.isGifPlaying) {
        // Pause by replacing with a static version
        const currentSrc = this.gifElement.src;
        this.gifElement.setAttribute('data-original-src', currentSrc);
        // Create a canvas to capture the current frame
        const canvas = document.createElement('canvas');
        canvas.width = this.gifElement.width;
        canvas.height = this.gifElement.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(this.gifElement, 0, 0);
        // Replace the GIF with a static image
        this.gifElement.src = canvas.toDataURL('image/png');
      } else {
        // Resume by restoring the original GIF source
        const originalSrc = this.gifElement.getAttribute('data-original-src');
        if (originalSrc) {
          this.gifElement.src = originalSrc;
        }
      }
      this.isGifPlaying = !this.isGifPlaying;
    }
  }

  // Method to restart GIF animation
  restartGif(): void {
    if (!this.gifElement) {
      this.gifElement = document.querySelector('.gif-viewer img') as HTMLImageElement;
    }

    if (this.gifElement) {
      // Trick to restart the GIF: clone and replace the element
      const originalSrc = this.gifElement.getAttribute('data-original-src') || this.gifElement.src;
      this.gifElement.src = '';
      setTimeout(() => {
        if (this.gifElement) {
          this.gifElement.src = originalSrc;
          this.isGifPlaying = true;
        }
      }, 10);
    }
  }

  ngOnInit(): void {
    // Initialize component

    // Check and record initial popup class state
    setTimeout(() => {
      const popupClass = document.querySelector('app-details-area.popup-mode');
      this._popupClassActive = !!popupClass;
    });

    // Subscribe to font size changes
    this.fontSizeService.fontSize$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(fontSize => {
      this.currentFontSize = fontSize;
      this.cdr.detectChanges();
    });

    // Check if we're on mobile view
    this.checkMobileView();

    // Listen for window resize events to update mobile view status
    window.addEventListener('resize', () => {
      this.checkMobileView();
    });

    // Subscribe to file operation events
    this.fileOpEvents.fileOperation$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      if (event === 'refresh') {
        console.log('DetailsAreaComponent: File replace event received, ready for refresh');
        // The MainAreaComponent will handle re-selecting the file
        // Just make sure any cached details are cleared
        if (this.selectedCard && this.selectedCard.fileData) {
          // Don't clear the entire card, but update file name display
          // by forcing change detection
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        }
      } else if (event === 'deleted') {
        console.log('DetailsAreaComponent: File delete event received, clearing view');
        // Clear the selected card to show the empty state
        this.selectedCard = null;
        this.cdr.detectChanges();
      }
    });

    // Signal that the details area has finished its initial loading
    setTimeout(() => {
      this.loadingService.setDetailsAreaLoaded();
    }, 500);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCard']) {
      // Reset image states when selected card changes
      this.resetImageStates();

      // Log the selected card for debugging
      if (this.selectedCard) {
        console.log('Details area received new card:', this.selectedCard.id);

        // When a new card is selected, ensure this component is visible
        // and scrolls into view if needed
        setTimeout(() => {
          // Force component to update
          this.cdr.detectChanges();

          // Try to make this component element visible in the viewport
          const detailsElement = document.querySelector('app-details-area');
          if (detailsElement) {
            console.log('Details area ensuring visibility');
            detailsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        console.log('Details area: No card selected');
      }
    }

    // React to fullscreen mode changes
    if (changes['isFullscreenMode']) {
      // Track if we're exiting fullscreen mode
      const wasFullscreen = changes['isFullscreenMode'].previousValue === true;
      const isNowFullscreen = changes['isFullscreenMode'].currentValue === true;

      // Update the view mode to match the fullscreen status
      this.viewMode = this.isFullscreenMode ? 'fullscreen' : 'split';

      // If we're no longer in fullscreen mode, ensure we're not tracking popup class mode
      if (!isNowFullscreen && this._popupClassActive) {
        this._popupClassActive = false;
      }

      console.log('Details area fullscreen mode changed, viewMode:', this.viewMode);

      // If entering fullscreen mode, initialize showMains based on parent value
      console.log('Details area fullscreen mode changed, showMains:', this.showMains);

      // If we have a file preview open, handle the transition
      if (this.selectedCard && this.isFilePreview()) {
        if (isNowFullscreen) {
          // If we're entering fullscreen mode and have a PDF open, apply zoom level
          if (this.getFileType() === 'pdf') {
            // Force a redraw of the component to apply PDF zoom
            setTimeout(() => {
              this.cdr.detectChanges();
              console.log('Applied 100% zoom to PDF in fullscreen mode');
            }, 100);
          }
        } else if (wasFullscreen) {
          // If we're exiting fullscreen mode and have a file preview
          console.log('Exiting fullscreen with file preview, scheduling refresh');
          setTimeout(() => {
            // Force a redraw to ensure proper rendering
            if (this.getFileType() === 'pdf') {
              // For PDFs, we might need to adjust the iframe
              const pdfIframe = document.querySelector('.file-iframe') as HTMLIFrameElement;
              if (pdfIframe) {
                // Force a reload of the iframe to refresh the PDF
                const currentSrc = pdfIframe.src;
                pdfIframe.src = 'about:blank';
                setTimeout(() => {
                  pdfIframe.src = currentSrc;
                  this.cdr.detectChanges();
                }, 50);
              } else {
                this.cdr.detectChanges();
              }
            } else {
              // For other file types
              this.cdr.detectChanges();
            }
          }, 300);
        }
      }
    }
  }

  // Track manually selected view mode
  private _manualViewMode: 'split' | 'fullscreen' | 'popup' = 'split';

  // Handle Show Mains checkbox change
  onShowMainsChange(event: any): void {
    this.showMains = event.checked;
    // Emit the showMains value to parent component
    this.showMainsChange.emit(this.showMains);
  }

  resetImageStates(): void {
    this.answerImageLoaded = false;
    this.explanationImageLoaded = false;
    this.questionImageLoaded = false;
    this.answerImageError = false;
    this.explanationImageError = false;
    this.questionImageError = false;

    // Reset Hindi image states
    this.hindiAnswerImageLoaded = false;
    this.hindiExplanationImageLoaded = false;
    this.hindiQuestionImageLoaded = false;
    this.hindiAnswerImageError = false;
    this.hindiExplanationImageError = false;
    this.hindiQuestionImageError = false;
  }

  onAnswerImageLoad(): void {
    this.answerImageLoaded = true;
    this.answerImageError = false;
    console.log('Answer image loaded successfully');
  }

  onAnswerImageError(): void {
    this.answerImageLoaded = true; // Still mark as "loaded" to remove spinner
    this.answerImageError = true;
    console.warn('Error loading answer image');
  }

  onExplanationImageLoad(): void {
    this.explanationImageLoaded = true;
    this.explanationImageError = false;
    console.log('Explanation image loaded successfully');
  }

  onExplanationImageError(): void {
    this.explanationImageLoaded = true; // Still mark as "loaded" to remove spinner
    this.explanationImageError = true;
    console.warn('Error loading explanation image');
  }

  onQuestionImageLoad(): void {
    this.questionImageLoaded = true;
    this.questionImageError = false;
    console.log('Question image loaded successfully');
  }

  onQuestionImageError(): void {
    this.questionImageLoaded = true; // Still mark as "loaded" to remove spinner
    this.questionImageError = true;
    console.warn('Error loading question image');
  }

  // Hindi image handling methods
  onHindiAnswerImageLoad(): void {
    this.hindiAnswerImageLoaded = true;
    this.hindiAnswerImageError = false;
    console.log('Hindi answer image loaded successfully');
  }

  onHindiAnswerImageError(): void {
    this.hindiAnswerImageLoaded = true;
    this.hindiAnswerImageError = true;
    console.warn('Error loading Hindi answer image');
  }

  onHindiExplanationImageLoad(): void {
    this.hindiExplanationImageLoaded = true;
    this.hindiExplanationImageError = false;
    console.log('Hindi explanation image loaded successfully');
  }

  onHindiExplanationImageError(): void {
    this.hindiExplanationImageLoaded = true;
    this.hindiExplanationImageError = true;
    console.warn('Error loading Hindi explanation image');
  }

  onHindiQuestionImageLoad(): void {
    this.hindiQuestionImageLoaded = true;
    this.hindiQuestionImageError = false;
    console.log('Hindi question image loaded successfully');
  }

  onHindiQuestionImageError(): void {
    this.hindiQuestionImageLoaded = true;
    this.hindiQuestionImageError = true;
    console.warn('Error loading Hindi question image');
  }

  // Helper method to extract the correct answer letter from the answer text
  getCorrectAnswerLetter(): string {
    if (!this.selectedCard) return '';

    // Since the Card interface doesn't have a correctAnswerIndex property,
    // we'll parse it from the answerText if possible
    const answerText = this.selectedCard.answerText || '';

    // Try to extract the answer letter (A, B, C, D) from the answer text
    const match = answerText.match(/^([A-D])[.\s]/i);
    if (match && match[1]) {
      return match[1].toUpperCase();
    }

    // If we can't extract it, return a placeholder
    return '?';
  }

  // Determine if the current card is a file preview
  isFilePreview(): boolean {
    if (!this.selectedCard || !this.selectedCard.fileData) {
      return false;
    }

    return true;
  }

  // Get the file type based on the file extension or URL
  getFileType(): string {
    if (this.selectedCard && this.selectedCard.fileData) {
      return this.selectedCard.fileData.fileType;
    } else if (this.selectedCard && this.selectedCard.imageUrl) {
      const url = this.selectedCard.imageUrl.toString();
      if (url.includes('.pdf')) return 'pdf';
      if (url.includes('.png')) return 'png';
      if (url.includes('.jpg') || url.includes('.jpeg')) return 'jpg';
      if (url.includes('.docx')) return 'docx';
      if (url.includes('.xlsx')) return 'xlsx';
      if (url.includes('.txt')) return 'txt';
      if (url.includes('.html')) return 'html';
      if (url.includes('_thumbnail')) return 'png_thumbnail';
      if (url.includes('.gif')) return 'gif';
      if (url.includes('youtube') || url.includes('youtu.be')) return 'youtube';
    }
    return '';
  }

  // Method to get the Google Docs viewer URL for the current file
  getGoogleDocsViewerUrl(): SafeResourceUrl | null {
    if (!this.selectedCard || !this.selectedCard.imageUrl) {
      return null;
    }

    // If we already created a Google Docs URL, return it
    if (this.googleDocsViewerUrl) {
      return this.googleDocsViewerUrl;
    }

    try {
      // For office documents, we need to set up Google Docs viewer
      const fileType = this.getFileType();

      if (fileType === 'docx' || fileType === 'xlsx' || fileType === 'pdf') {
        // Extract the URL string from SafeResourceUrl
        let originalUrl: string;

        if (typeof this.selectedCard.imageUrl === 'object' &&
            this.selectedCard.imageUrl !== null &&
            'changingThisBreaksApplicationSecurity' in this.selectedCard.imageUrl) {
          // Get URL from SafeResourceUrl
          originalUrl = (this.selectedCard.imageUrl as any).changingThisBreaksApplicationSecurity;
        } else {
          // Convert to string if it's not a SafeResourceUrl
          originalUrl = this.selectedCard.imageUrl.toString();
        }

        // Blob URLs can't be used with Google Docs viewer as they're local to the browser
        // So we use a fallback message instead
        if (originalUrl.startsWith('blob:')) {
          console.warn('Blob URLs cannot be used with Google Docs viewer');
          this.googleDocsViewerError = true;
          return null;
        }

        // For regular URLs, create a Google Docs viewer URL
        // Format: https://docs.google.com/viewer?url=ENCODED_URL&embedded=true
        const encodedUrl = encodeURIComponent(originalUrl);
        const googleDocsUrl = `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;

        console.log('Created Google Docs viewer URL:', {
          fileType,
          originalUrl,
          googleDocsUrl
        });

        // Save and return the safe URL
        this.googleDocsViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(googleDocsUrl);
        return this.googleDocsViewerUrl;
      }
    } catch (error) {
      console.error('Error creating Google Docs viewer URL:', error);
      this.googleDocsViewerError = true;
      return null;
    }

    return null;
  }

  // Handle Google Docs viewer load event
  onGoogleDocsViewerLoad(): void {
    this.googleDocsViewerLoaded = true;
    this.googleDocsViewerError = false;
    this.officeFileLoading = false;
    console.log('Google Docs viewer loaded successfully');
  }

  // Handle Google Docs viewer error event
  onGoogleDocsViewerError(): void {
    this.googleDocsViewerLoaded = true; // Mark as "loaded" to remove spinner
    this.googleDocsViewerError = true;
    this.officeFileLoading = false;
    console.warn('Error loading Google Docs viewer');
  }

  // Method to check if the URL is using Google Docs viewer
  isGoogleDocsUrl(): boolean {
    if (!this.selectedCard || !this.selectedCard.imageUrl) return false;

    // Check if the URL is a SafeResourceUrl object
    if (typeof this.selectedCard.imageUrl === 'object' &&
        this.selectedCard.imageUrl !== null &&
        'changingThisBreaksApplicationSecurity' in this.selectedCard.imageUrl) {
      // Extract the raw URL from the SafeResourceUrl using type assertion
      const safeUrl = this.selectedCard.imageUrl as any;
      const url = safeUrl.changingThisBreaksApplicationSecurity;

      // Check if it's a Google Docs viewer URL
      return typeof url === 'string' && url.includes('docs.google.com/viewer');
    }

    return false;
  }

  // More comprehensive check for online viewers (Google Docs or Office Online)
  isOnlineViewerUrl(): boolean {
    if (!this.selectedCard || !this.selectedCard.imageUrl) return false;

    // Check if the URL is a SafeResourceUrl object
    if (typeof this.selectedCard.imageUrl === 'object' &&
        this.selectedCard.imageUrl !== null &&
        'changingThisBreaksApplicationSecurity' in this.selectedCard.imageUrl) {
      // Extract the raw URL from the SafeResourceUrl using type assertion
      const safeUrl = this.selectedCard.imageUrl as any;
      const url = safeUrl.changingThisBreaksApplicationSecurity;

      // Check if it's using any of the online viewers
      return typeof url === 'string' && (
        url.includes('docs.google.com/viewer') ||
        url.includes('view.officeapps.live.com')
      );
    }

    return false;
  }

  // Handle tab selection change
  onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
    console.log('Tab changed to:', this.selectedTabIndex);
  }

  // Method to navigate back to main area on mobile
  goBackToMain(): void {
    this.showMains = true;
    this.backToMain.emit();
  }

  // Get the download URL for the file
  getDownloadUrl(): string {
    if (!this.selectedCard || !this.selectedCard.imageUrl) {
      return '';
    }

    return this.selectedCard.imageUrl.toString();
  }

  /**
   * Check if the current view is mobile based on window width
   */
  private checkMobileView(): void {
    this.isMobileView = window.innerWidth < 768;
  }

  // Handle Office viewer load event
  onOfficeViewerLoad(): void {
    this.officeFileLoading = false;
    this.officeViewerError = false;
    console.log('Office viewer loaded successfully');
  }

  // Handle Office viewer error event
  onOfficeViewerError(): void {
    this.officeFileLoading = false;
    this.officeViewerError = true;
    console.warn('Error loading Office viewer');
  }

  // Helper method to get content length safely for template
  getContentLength(): string {
    if (!this.selectedCard || !this.selectedCard.fileData || !this.selectedCard.fileData.content) {
      return 'N/A';
    }

    if (typeof this.selectedCard.fileData.content === 'string') {
      return (this.selectedCard.fileData.content as string).length.toString();
    }

    return 'N/A (not a string)';
  }

  // Implement OnDestroy to clean up subscriptions
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Get the appropriate icon for the current view mode
  getViewModeIcon(): string {
    // Use the internal popup state tracker instead of querying DOM
    if (this._popupClassActive) {
      return 'open_in_new';
    }

    // Otherwise use the viewMode property
    switch (this.viewMode) {
      case 'fullscreen':
        return 'fullscreen';
      case 'popup':
        return 'open_in_new';
      case 'split':
        return 'view_column';
      default:
        return 'view_carousel';
    }
  }

  // Set the view mode and emit change event
  setViewMode(mode: 'split' | 'fullscreen' | 'popup'): void {
    // Store the manually selected view mode
    this._manualViewMode = mode;
    console.log('in setViewMode');
    console.log('Previous viewMode:', this.viewMode);
    console.log('Setting to:', mode);

    // Always update the current view mode first
    this.viewMode = mode;
    console.log('After setting, viewMode is:', this.viewMode);

    // Update popup class state
    if (mode === 'popup') {
      // When entering popup mode, prepare for class to be applied
      this._popupClassActive = true;
    } else if (this._popupClassActive) {
      // When leaving popup mode, prepare for class to be removed
      this._popupClassActive = false;
    }

    // Only emit fullscreen status changes to parent component
    if (mode === 'fullscreen') {
      // Enter fullscreen mode
      this.viewModeChange.emit('fullscreen');
    } else if (mode === 'popup') {
      // Enter popup mode
      this.viewModeChange.emit('popup');
    } else {
      // Exit fullscreen/popup mode
      this.viewModeChange.emit('split');
    }

    // Force update after view mode change
    setTimeout(() => {
      this.cdr.detectChanges();
      console.log('After detectChanges, viewMode is:', this.viewMode);
    }, 0);

    // Special handling for specific file types
    if (this.selectedCard && this.isFilePreview()) {
      if (mode === 'fullscreen' || mode === 'popup') {
        // When entering fullscreen or popup mode with a PDF
        if (this.getFileType() === 'pdf') {
          setTimeout(() => {
            const pdfIframe = document.querySelector('.file-iframe') as HTMLIFrameElement;
            if (pdfIframe) {
              this.cdr.detectChanges();
            }
          }, 200);
        }
      }
    }
  }

  // File operation methods
  onDownloadFile(): void {
    console.log('Download button clicked in details-area');
    if (this.isFilePreview() && this.selectedCard && this.selectedCard.fileData) {
      const fileType = this.selectedCard.fileData.fileType;
      const bundleId = 1; // Use appropriate bundleId if dynamic
      this.fileService.downloadFileByType(bundleId, fileType).subscribe((blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.selectedCard?.fileData?.fileName || `file.${fileType}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      });
    }
  }

  onReplaceFile(): void {
    console.log('Replace button clicked in details-area');
    if (this.isFilePreview() && this.selectedCard && this.selectedCard.fileData) {
      const fileType = this.selectedCard.fileData.fileType;
      const bundleId = 1; // Use appropriate bundleId if dynamic
      // Create a file input dialog
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.' + fileType;
      fileInput.click();
      fileInput.addEventListener('change', (event) => {
        const files = fileInput.files;
        if (files && files.length > 0) {
          const file = files[0];
          const formData = new FormData();
          formData.append('file', file);
          this.fileService.replaceFileInBundle(bundleId, fileType, formData).subscribe(() => {
            // Optionally refresh UI or show success
            console.log('File replaced successfully');
            this.fileOpEvents.emit('refresh');
          });
        }
      });
    }
  }

  onDeleteFile(): void {
    console.log('Delete button clicked in details-area');
    if (this.isFilePreview() && this.selectedCard && this.selectedCard.fileData) {
      const fileType = this.selectedCard.fileData.fileType;
      const bundleId = 1; // Use appropriate bundleId if dynamic
      if (confirm(`Are you sure you want to delete the ${fileType} file?`)) {
        this.fileService.deleteFileFromBundle(bundleId, fileType).subscribe(() => {
          // Clear selected card after delete
          this.selectedCard = null;
          this.cdr.detectChanges();
          this.fileOpEvents.emit('deleted');
          console.log('File deleted successfully');
        });
      }
    }
  }

  // Method to close the details window
  closeDetailsWindow(): void {
    // If in mobile view, go back to main
    console.log('in closeDetailsWindow');
    console.log('Current viewMode:', this.viewMode);
    console.log('isFullscreenMode:', this.isFullscreenMode);

    if (this.isMobileView) {
      this.goBackToMain();
      return;
    }

    // Check if we're in popup mode (using our new helper)
    const isInPopupMode = this.isInPopupMode();
    console.log('Is in popup mode:', isInPopupMode);

    // Find the main-area component and update its properties directly
    const mainAreaComponent = document.querySelector('app-main-area');
    if (mainAreaComponent) {
      try {
        // Access the component's property using Angular's __ngContext__
        const componentInstance = (mainAreaComponent as any).__ngContext__.instance;
        if (componentInstance) {
          componentInstance.showDetails = false;
          console.log('Successfully updated MainAreaComponent properties directly');
        }
      } catch (e) {
        console.error('Error updating MainAreaComponent properties:', e);
      }
    }

    // In popup or fullscreen mode, always revert to split view
    if (isInPopupMode || this.isFullscreenMode || this.viewMode === 'popup' || this.viewMode === 'fullscreen') {
      console.log('Reverting to split mode');
      // Make sure to update our popup class state
      this._popupClassActive = false;
      this.setViewMode('split');
    } else {
      // Only if we're truly in split mode, hide the details
      console.log('Hiding details in split mode');
      this.hideDetails.emit();
    }

    // Reset the selected card
    this.selectedCard = null;
  }

  // Check if we're in popup mode (avoiding direct DOM queries)
  isInPopupMode(): boolean {
    return this._popupClassActive || this.viewMode === 'popup';
  }

  // Update popup class state (call this when the class is added/removed)
  updatePopupClassState(active: boolean): void {
    this._popupClassActive = active;
  }

  // Toggle view menu layout
  toggleViewMenuLayout(event: any): void {
    this.viewMenuLayoutDirection = event.checked ? 'horizontal' : 'vertical';
    // Force update to reflect changes
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  // Open YouTube video in a new tab
  openInYouTube(): void {
    if (this.selectedCard && this.selectedCard.fileData && this.selectedCard.fileData.youtubeData) {
      const youtubeUrl = this.selectedCard.fileData.youtubeData.watch_url || this.selectedCard.fileData.originalUrl;
      if (youtubeUrl) {
        window.open(youtubeUrl, '_blank');
      }
    }
  }
}
